import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';

const LOGO_SRC = '/assets/zschimmer-schwarz-logo.png';

const adminNavItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { path: '/admin/solicitacoes-epi', label: 'Solicitações de EPI', icon: 'ri-tools-line' },
  { path: '/admin/incidentes', label: 'Incidentes', icon: 'ri-alarm-warning-line' },
  { path: '/admin/denuncias', label: 'Denúncias', icon: 'ri-shield-user-line' },
  { path: '/admin/usuarios', label: 'Usuários', icon: 'ri-group-line' },
  { path: '/admin/questionarios', label: 'Questionários', icon: 'ri-survey-line' },
  { path: '/admin/relatorios', label: 'Relatórios', icon: 'ri-file-chart-line' },
];

const adminFormNavItems = [
  { path: '/colaborador/epi', label: 'Solicitação de EPI', icon: 'ri-tools-line' },
  { path: '/colaborador/incidente', label: 'Registro de Incidente', icon: 'ri-alarm-warning-line' },
  { path: '/colaborador/denuncia', label: 'Canal de Denúncias', icon: 'ri-shield-user-line' },
];

const colabNavItems = [
  { path: '/colaborador/epi', label: 'Solicitação de EPI', icon: 'ri-tools-line' },
  { path: '/colaborador/incidente', label: 'Registro de Incidente', icon: 'ri-alarm-warning-line' },
  { path: '/colaborador/denuncia', label: 'Canal de Denúncias', icon: 'ri-shield-user-line' },
  { path: '/colaborador/historico', label: 'Meu Histórico', icon: 'ri-history-line' },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { notifications, unreadCount, markAllAsSeen } = useNotifications();

  const isAdmin = user?.perfil === 'admin';
  const navItems = isAdmin ?adminNavItems : colabNavItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const notificationBell = (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setNotificationsOpen((open) => !open);
          markAllAsSeen();
        }}
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-portal-gray-500 hover:bg-portal-gray-100 hover:text-portal-purple"
        title="Notificações"
      >
        <i className="ri-notification-3-line text-lg"></i>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 9 ?'9+' : unreadCount}
          </span>
        )}
      </button>
      {notificationsOpen && (
        <div className="absolute left-0 mt-2 w-72 rounded-xl border border-portal-gray-200 bg-white shadow-lg z-40 overflow-hidden">
          <div className="px-4 py-3 border-b border-portal-gray-100">
            <p className="text-sm font-semibold text-portal-gray-800">Notificações</p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ?(
              <p className="px-4 py-6 text-sm text-portal-gray-400 text-center">Nenhuma notificação.</p>
            ) : notifications.map((item) => (
              <div key={item.id} className="px-4 py-3 border-b border-portal-gray-50 last:border-0">
                <p className="text-xs font-semibold text-portal-gray-800">{item.title}</p>
                <p className="text-xs text-portal-gray-500 mt-0.5">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-portal-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-portal-gray-200 fixed h-full z-20">
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-portal-gray-100">
          <img
            src={LOGO_SRC}
            alt="Zschimmer & Schwarz Brasil"
            className="h-8 w-auto object-contain mr-3"
          />
          <div>
            <h1 className="text-sm font-bold text-portal-gray-900 leading-tight">Portal do Colaborador</h1>
            <p className="text-[10px] text-portal-gray-400 leading-tight">Segurança do Trabalho</p>
          </div>
        </div>

        {/* User Info */}
        <div className="px-4 py-3 border-b border-portal-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-portal-purple-100 flex items-center justify-center shrink-0">
              <i className="ri-user-line text-portal-purple text-sm"></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-portal-gray-800 truncate">{user?.name}</p>
              <p className="text-[10px] text-portal-gray-400 truncate">{user?.cpf} - {user?.setor}</p>
            </div>
            {notificationBell}
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {isAdmin && (
            <>
              <p className="text-[10px] font-semibold text-portal-gray-400 uppercase tracking-wider px-3 mb-2">
                Responder
              </p>
              <ul className="space-y-0.5 mb-4">
                {adminFormNavItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                          isActive
                            ?'bg-portal-purple text-white'
                            : 'text-portal-gray-600 hover:bg-portal-gray-100 hover:text-portal-gray-800'
                        }`}
                      >
                        <i className={`${item.icon} text-sm ${isActive ?'text-white' : 'text-portal-gray-400'}`}></i>
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          <p className="text-[10px] font-semibold text-portal-gray-400 uppercase tracking-wider px-3 mb-2">
            {isAdmin ?'Administração' : 'Formulários'}
          </p>
          <ul className="space-y-0.5">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      isActive
                        ?'bg-portal-purple text-white'
                        : 'text-portal-gray-600 hover:bg-portal-gray-100 hover:text-portal-gray-800'
                    }`}
                  >
                    <i className={`${item.icon} text-sm ${isActive ?'text-white' : 'text-portal-gray-400'}`}></i>
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-portal-gray-100">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-portal-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors w-full"
          >
            <i className="ri-logout-box-r-line text-sm"></i>
            Sair
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-portal-gray-200 z-30 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img
            src={LOGO_SRC}
            alt="Zschimmer & Schwarz Brasil"
            className="h-6 w-auto object-contain"
          />
          <span className="text-sm font-bold text-portal-gray-900">Portal do Colaborador</span>
        </div>
        <div className="flex items-center gap-2">
          {notificationBell}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="w-8 h-8 flex items-center justify-center text-portal-gray-600"
        >
          <i className={`${isMobileMenuOpen ?'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-black/30 z-25" onClick={() => setIsMobileMenuOpen(false)}></div>
      )}

      {/* Mobile Sidebar */}
      <div className={`md:hidden fixed inset-y-0 left-0 w-64 bg-white border-r border-portal-gray-200 z-30 transform transition-transform duration-200 ${isMobileMenuOpen ?'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-14 flex items-center px-4 border-b border-portal-gray-100">
          <div className="flex items-center gap-2">
            <img
              src={LOGO_SRC}
              alt="Zschimmer & Schwarz Brasil"
              className="h-6 w-auto object-contain"
            />
            <span className="text-sm font-bold text-portal-gray-900">Portal do Colaborador</span>
          </div>
        </div>

        <div className="px-4 py-3 border-b border-portal-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-portal-purple-100 flex items-center justify-center shrink-0">
              <i className="ri-user-line text-portal-purple text-sm"></i>
            </div>
            <div>
              <p className="text-xs font-semibold text-portal-gray-800">{user?.name}</p>
              <p className="text-[10px] text-portal-gray-400">{user?.cpf}</p>
            </div>
          </div>
        </div>

        <nav className="px-3 py-4">
          {isAdmin && (
            <>
              <p className="text-[10px] font-semibold text-portal-gray-400 uppercase tracking-wider px-3 mb-2">
                Responder
              </p>
              <ul className="space-y-0.5 mb-4">
                {adminFormNavItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                          isActive
                            ?'bg-portal-purple text-white'
                            : 'text-portal-gray-600 hover:bg-portal-gray-100 hover:text-portal-gray-800'
                        }`}
                      >
                        <i className={`${item.icon} text-sm ${isActive ?'text-white' : 'text-portal-gray-400'}`}></i>
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          <p className="text-[10px] font-semibold text-portal-gray-400 uppercase tracking-wider px-3 mb-2">
            {isAdmin ?'Administração' : 'Formulários'}
          </p>
          <ul className="space-y-0.5">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      isActive
                        ?'bg-portal-purple text-white'
                        : 'text-portal-gray-600 hover:bg-portal-gray-100 hover:text-portal-gray-800'
                    }`}
                  >
                    <i className={`${item.icon} text-sm ${isActive ?'text-white' : 'text-portal-gray-400'}`}></i>
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-portal-gray-100 bg-white">
          <button
            onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-portal-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors w-full"
          >
            <i className="ri-logout-box-r-line text-sm"></i>
            Sair
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 min-h-screen pt-14 md:pt-0">
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}

